import type { StemManifest } from "../types";
import {
  AcousticDrumSampler,
  type AcousticDrumVoice,
} from "./acoustic-drum-sampler";
import { createMasterInput } from "./audio-master-bus";

type Channel = "guitar" | "bass" | "drums";

export class StudioAudio {
  private context?: AudioContext;
  private manifest?: StemManifest;
  private buffers = new Map<string, AudioBuffer>();
  private sources: AudioBufferSourceNode[] = [];
  private channels = new Map<Channel, GainNode>();
  private channelEnabled: Record<Channel, boolean> = {
    guitar: true,
    bass: true,
    drums: true,
  };
  private channelLevelDb: Record<Channel, number> = {
    guitar: 0,
    bass: 0,
    drums: 0,
  };
  private styleGains = new Map<string, GainNode>();
  private sandboxSampler?: AcousticDrumSampler;
  private sandboxEnabled = false;
  private manifestBase = "";
  private styleMap: Record<string, string> = {};
  private activeStyle = "";
  private epoch = 0;
  private frame = 0;
  playing = false;
  onProgress?: (progress: number) => void;

  async load(manifestUrl: string, styles: Record<string, string>, initialStyle: string): Promise<void> {
    if (this.manifest) return;
    const response = await fetch(manifestUrl);
    if (!response.ok) throw new Error(`Studio manifest returned ${response.status}`);
    this.manifest = await response.json() as StemManifest;
    this.manifestBase = manifestUrl.slice(0, manifestUrl.lastIndexOf("/") + 1);
    this.styleMap = styles;
    this.activeStyle = initialStyle;
    this.context = new AudioContext({ sampleRate: this.manifest.sampleRate });
    const master = createMasterInput(this.context, this.manifest.masterBus);
    for (const channel of ["guitar", "bass", "drums"] as Channel[]) {
      const gain = this.context.createGain();
      gain.gain.value = 1;
      gain.connect(master);
      this.channels.set(channel, gain);
    }
    if (this.manifest.sandboxKit) {
      this.sandboxSampler = new AcousticDrumSampler(
        this.context,
        this.manifestBase,
        this.manifest.sandboxKit,
        master,
      );
    }
    for (const [profile, style] of Object.entries(styles)) {
      const gain = this.context.createGain();
      gain.gain.value = profile === initialStyle ? 1 : 0;
      gain.connect(this.channels.get("drums")!);
      this.styleGains.set(style, gain);
    }
    await Promise.all(Object.entries(this.manifest.stems).map(async ([name, filename]) => {
      const stem = await fetch(this.manifestBase + filename);
      if (!stem.ok) throw new Error(`${filename} returned ${stem.status}`);
      const decoded = await this.context!.decodeAudioData(await stem.arrayBuffer());
      if (Math.abs(decoded.duration - this.manifest!.durationSeconds) > 0.05) {
        throw new Error(`${filename} is not aligned to the studio loop`);
      }
      this.buffers.set(name, decoded);
    }));
  }

  async loadSandboxKit(): Promise<void> {
    if (!this.sandboxSampler) {
      throw new Error("No acoustic Sandbox kit is available for this study");
    }
    await this.sandboxSampler.load();
    this.sandboxSampler.setEnabled(this.sandboxEnabled);
  }

  async play(): Promise<void> {
    if (!this.context || !this.manifest || this.playing) return;
    await this.context.resume();
    this.epoch = this.context.currentTime + 0.06;
    const start = (name: string, destination: AudioNode) => {
      const source = this.context!.createBufferSource();
      source.buffer = this.buffers.get(name)!;
      source.loop = true;
      source.loopEnd = this.manifest!.durationSeconds;
      source.connect(destination);
      source.start(this.epoch);
      this.sources.push(source);
    };
    start("guitar", this.channels.get("guitar")!);
    start("bass", this.channels.get("bass")!);
    for (const style of Object.values(this.styleMap)) start(`drums__${style}`, this.styleGains.get(style)!);
    this.playing = true;
    this.animate();
  }

  stop(): void {
    this.playing = false;
    cancelAnimationFrame(this.frame);
    for (const source of this.sources) {
      try { source.stop(); } catch { /* already stopped */ }
      source.disconnect();
    }
    this.sources = [];
    this.sandboxSampler?.stop();
    this.onProgress?.(0);
  }

  selectStyle(profile: string): void {
    this.activeStyle = profile;
    if (!this.context) return;
    const active = this.styleMap[profile];
    for (const [style, gain] of this.styleGains) {
      gain.gain.cancelScheduledValues(this.context.currentTime);
      gain.gain.setTargetAtTime(style === active ? 1 : 0, this.context.currentTime, 0.012);
    }
  }

  setChannel(channel: Channel, enabled: boolean): void {
    this.channelEnabled[channel] = enabled;
    this.applyChannelGain(channel);
  }

  setChannelLevel(channel: Channel, levelDb: number): void {
    this.channelLevelDb[channel] = levelDb;
    this.applyChannelGain(channel);
  }

  private applyChannelGain(channel: Channel): void {
    const gain = this.channels.get(channel);
    if (!gain || !this.context) return;
    gain.gain.cancelScheduledValues(this.context.currentTime);
    gain.gain.setTargetAtTime(
      this.channelEnabled[channel] ? 10 ** (this.channelLevelDb[channel] / 20) : 0,
      this.context.currentTime,
      0.012,
    );
  }

  setSandboxDrums(enabled: boolean): void {
    this.sandboxEnabled = enabled;
    this.sandboxSampler?.setEnabled(enabled);
  }

  setSandboxDrumLevel(levelDb: number): void {
    this.sandboxSampler?.setLevelDb(levelDb);
  }

  setSandboxVoiceLevel(voice: AcousticDrumVoice, levelDb: number): void {
    this.sandboxSampler?.setVoiceLevelDb(voice, levelDb);
  }

  playDrum(voice: AcousticDrumVoice, level: number, at?: number): void {
    this.sandboxSampler?.hit(voice, level, at);
  }

  millisecondsUntilStart(): number {
    if (!this.playing || !this.context) return 0;
    return Math.max(0, (this.epoch - this.context.currentTime) * 1_000);
  }

  private animate = (): void => {
    if (!this.playing || !this.context || !this.manifest) return;
    const elapsed = Math.max(0, this.context.currentTime - this.epoch);
    this.onProgress?.((elapsed % this.manifest.durationSeconds) / this.manifest.durationSeconds);
    this.frame = requestAnimationFrame(this.animate);
  };

  destroy(): void {
    this.stop();
    this.sandboxSampler?.destroy();
    this.sandboxSampler = undefined;
    void this.context?.close();
  }
}
